import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Input,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { Download, Edit2, Plus, Trash2, Upload } from 'lucide-react';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useCommandPresetStore, effectiveNames } from '../../state/useCommandPresetStore';
import { CANONICAL_COMMANDS, normalizeCommandName } from '../../core/commands/canonicalCommands';
import { downloadTextFile } from '../../services/fileSystem/fileAccess';
import { notify } from '../../utils/notify';
import { useAppStore } from '../../state/store';
import { AcadPgpImportButton } from '../ui/AcadPgpImportButton';

/**
 * Command Presets dialog — the table view over the canonical command system.
 *
 * Left column manages presets (built-ins + customs: new/duplicate/rename/
 * delete/import/export); the main table shows one row per canonical action
 * with the CAD/3D built-in spellings and an editable "active names"
 * cell (comma-separated, primary first). Built-in spellings always stay
 * resolvable — editing only changes what the user types and what autocomplete
 * leads with (docs/architecture/command-preset-system.md).
 */
export function CommandPresetsDialog() {
    const { t } = useTranslation();
    const closeDialog = useAppStore((s) => s.closeManageDialog);
    // Data via narrow selectors; actions read through getState() at call time.
    // The previous whole-store destructure re-rendered the dialog table on
    // every preset mutation (and every unrelated store write).
    const presets = useCommandPresetStore((s) => s.presets);
    const activePresetId = useCommandPresetStore((s) => s.activePresetId);
    const store = {
        renamePreset: useCommandPresetStore.getState().renamePreset,
        createPreset: useCommandPresetStore.getState().createPreset,
        duplicatePreset: useCommandPresetStore.getState().duplicatePreset,
        deletePreset: useCommandPresetStore.getState().deletePreset,
        setActivePreset: useCommandPresetStore.getState().setActivePreset,
        resetPreset: useCommandPresetStore.getState().resetPreset,
        setOverride: useCommandPresetStore.getState().setOverride,
    };

    const [selectedId, setSelectedId] = useState(activePresetId);
    const [search, setSearch] = useState('');
    const [onlyChanged, setOnlyChanged] = useState(false);
    const [renamingId, setRenamingId] = useState<string | null>(null); const [renameValue, setRenameValue] = useState(''); const fileInputRef = useRef<HTMLInputElement>(null);

    const preset = presets.find((p) => p.id === selectedId) ?? presets[0];

    /** Cross-action name collisions inside the selected preset (per row). */
    const collisions = useMemo(() => {
        const seen = new Map<string, string>(); const bad = new Set<string>();
        if (!preset) return bad;
        for (const cmd of CANONICAL_COMMANDS) {
            for (const name of effectiveNames(preset, cmd)) {
                const key = normalizeCommandName(name);
                if (seen.has(key) && seen.get(key) !== cmd.id) bad.add(cmd.id);
                else seen.set(key, cmd.id);
            }
        }
        return bad;
    }, [preset]);

    const rows = useMemo(() => {
        if (!preset) return [];
        const q = search.trim().toLowerCase();
        return CANONICAL_COMMANDS.filter((cmd) => {
            if (onlyChanged && !preset.overrides[cmd.id]) return false;
            if (!q) return true;
            return (
                cmd.label.toLowerCase().includes(q) ||
                cmd.id.toLowerCase().includes(q) ||
                cmd.description.toLowerCase().includes(q) ||
                [...cmd.names['2d'], ...cmd.names['3d']].some((n) => n.toLowerCase().includes(q))
            );
        });
    }, [preset, search, onlyChanged]);

    const handleExport = useCallback(() => {
        if (!preset) return;
        const json = useCommandPresetStore.getState().exportPreset(preset.id);
        if (json) downloadTextFile(`${preset.name.replace(/\s+/g, '-')}.yapcommands.json`, json);
    }, [preset]);

    const handleImportFile = useCallback(async (file: File) => {
        const text = await file.text();
        const result = useCommandPresetStore.getState().importPreset(text);
        notify(result.message, result.ok ? 'success' : 'error');
    }, []);

    if (!preset) return null;

    return (
        <ManageDialogFrame
            title={t('manage.commandPresets')}
            isOpen
            onClose={closeDialog}
            size="4xl"
            footer={<ManageDialogCloseFooter onClose={closeDialog} />}
        >
            <Flex gap={4} minH="60vh">
                {/* --- Preset column --- */}
                <VStack align="stretch" spacing={2} w="220px" flexShrink={0}>
                    <Text fontSize="xs" fontWeight="bold" color="text.secondary">{t('manage.pRESETS')}</Text>
                    {presets.map((p) => (
                        <Box
                            key={p.id}
                            p={2}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={p.id === preset.id ? 'brand.500' : 'border.base'}
                            bg={p.id === preset.id ? 'brand.900' : 'transparent'}
                            cursor="pointer"
                            _hover={{ borderColor: 'brand.500' }}
                            onClick={() => { setSelectedId(p.id); setRenamingId(null); }}
                        >
                            {renamingId === p.id ? (
                                <Input
                                    size="xs"
                                    autoFocus
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onBlur={() => {
                                        store.renamePreset(p.id, renameValue);
                                        setRenamingId(null);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    }}
                                />
                            ) : (
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium">{p.name}</Text>
                                    {p.isBuiltIn && (
                                        <Text fontSize="xs" color="text.secondary">{t('manage.builtIn')}</Text>
                                    )}
                                </VStack>
                            )}
                        </Box>
                    ))}
                    <HStack spacing={1}>
                        <Tooltip label={t('manage.newCustomPresetFromTheActiveOne')}>
                            <IconButton
                                aria-label={t('manage.newPreset')}
                                icon={<Plus size={14} />}
                                size="sm"
                                variant="outline"
                                onClick={() => store.createPreset(`Custom ${presets.filter((x) => !x.isBuiltIn).length + 1}`)}
                            />
                        </Tooltip>
                        <Tooltip label={t('manage.duplicateSelected')}>
                            <IconButton
                                aria-label={t('manage.duplicatePreset')}
                                icon={<Plus size={14} />}
                                size="sm"
                                variant="outline"
                                onClick={() => store.duplicatePreset(preset.id, `${preset.name} Copy`)}
                            />
                        </Tooltip>
                        <Tooltip label={t('manage.renameCustomPreset')}>
                            <IconButton
                                aria-label={t('manage.renamePreset')}
                                icon={<Edit2 size={14} />}
                                size="sm"
                                variant="outline"
                                isDisabled={preset.isBuiltIn}
                                onClick={() => { setRenamingId(preset.id); setRenameValue(preset.name); }}
                            />
                        </Tooltip>
                        <Tooltip label={t('manage.deleteCustomPreset')}>
                            <IconButton
                                aria-label={t('manage.deletePreset')}
                                icon={<Trash2 size={14} />}
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                isDisabled={preset.isBuiltIn}
                                onClick={() => store.deletePreset(preset.id)}
                            />
                        </Tooltip>
                    </HStack>
                    <HStack spacing={1}>
                        <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<Upload size={12} />}
                            onClick={() => fileInputRef.current?.click()}
                        >{t('manage.import')}</Button>
                        <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<Download size={12} />}
                            onClick={handleExport}
                        >{t('manage.export')}</Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json,application/json"
                            className="hidden-file-input"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleImportFile(f);
                                e.target.value = '';
                            }}
                        />
                    </HStack>
                    <Box h={2} />
                    <AcadPgpImportButton />
                    <Box h={2} />
                    <Button
                        size="sm"
                        colorScheme={activePresetId === preset.id ? 'green' : 'brand'}
                        variant={activePresetId === preset.id ? 'solid' : 'outline'}
                        onClick={() => {
                            store.setActivePreset(preset.id);
                            notify(`Active command preset: ${preset.name}`, 'success');
                        }}
                    >
                        {activePresetId === preset.id ? 'Active ✓' : 'Set Active'}
                    </Button>
                    {preset.isBuiltIn && (
                        <Button size="xs" variant="ghost" onClick={() => store.resetPreset(preset.id)}>{t('manage.resetToDefaults')}</Button>
                    )}
                    {preset.notes && (
                        <Text fontSize="xs" color="text.secondary">{preset.notes}</Text>
                    )}
                </VStack>

                {/* --- Command table --- */}
                <VStack align="stretch" spacing={2} flex={1} minW={0}>
                    <HStack spacing={2}>
                        <Input
                            size="sm"
                            placeholder={t('manage.searchCommandsIdsSpellings')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <Button
                            size="sm"
                            variant={onlyChanged ? 'solid' : 'outline'}
                            colorScheme="brand"
                            flexShrink={0}
                            onClick={() => setOnlyChanged((v) => !v)}
                        >{t('manage.onlyChanged')}</Button>
                    </HStack>
                    <Box
                        flex={1}
                        overflowY="auto"
                        borderWidth="1px"
                        borderColor="border.base"
                        borderRadius="md"
                    >
                        <Table size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                            <Thead>
                                <Tr>
                                    {['Action', 'CAD', '3D', 'Active names (primary first)'].map((h) => (
                                        <Th
                                            key={h}
                                            color="text.secondary"
                                            position="sticky"
                                            top="0"
                                            bg="bg.panel"
                                            zIndex={1}
                                            whiteSpace="nowrap"
                                        >
                                            {h}
                                        </Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {rows.map((cmd) => (
                                    <PresetRow
                                        key={cmd.id}
                                        cmd={cmd}
                                        preset={preset}
                                        collision={collisions.has(cmd.id)}
                                        onChange={(names) => store.setOverride(preset.id, cmd.id, names)}
                                    />
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                    <Text fontSize="xs" color="text.secondary">{t('manage.builtInSpellingsAlwaysWorkRegardlessOfThePresetEditingActiveNamesCh')}</Text>
                </VStack>
            </Flex>
        </ManageDialogFrame>
    );
}

/** One row of the table; the active-names cell commits on blur/Enter. */
function PresetRow({
    cmd,
    preset,
    collision,
    onChange,
}: {
    cmd: (typeof CANONICAL_COMMANDS)[number];
    preset: ReturnType<typeof useCommandPresetStore.getState>['presets'][number];
    collision: boolean;
    onChange: (names: string[]) => void;
}) {
    const { t } = useTranslation();
    const override = preset.overrides[cmd.id];
    const value = override ? override.join(', ') : '';

    return (
        <Tr borderBottomWidth="1px" borderColor="border.base">
            <Td>
                <Text fontWeight="medium">{cmd.label}</Text>
                <Text fontSize="xs" color="text.secondary">{cmd.description}</Text>
            </Td>
            <Td whiteSpace="nowrap" fontFamily="mono">
                {cmd.names['2d'].join(', ')}
            </Td>
            <Td whiteSpace="nowrap" fontFamily="mono">
                {cmd.names['3d'].join(', ')}
            </Td>
            <Td minW="180px">
                <Input
                    size="sm"
                    fontFamily="mono"
                    defaultValue={value}
                    placeholder={effectiveNames(preset, cmd).slice(0, 2).join(', ')}
                    borderColor={collision ? 'red.400' : undefined}
                    onBlur={(e) => {
                        const names = e.target.value
                            .split(',')
                            .map((n) => n.trim())
                            .filter(Boolean);
                        onChange(names);
                    }}
                    onKeyDown={(e) => {
                        const { t } = useTranslation();
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        e.stopPropagation();
                    }}
                />
                {override && (
                    <HStack spacing={1}>
                        <Text fontSize="xs" color="brand.500">{t('manage.custom')}</Text>
                        <Tooltip label={t('manage.revertToFamilyDefaults')}>
                            <Text
                                as="button"
                                fontSize="xs"
                                color="text.secondary"
                                onClick={() => {
                                    const { t } = useTranslation();
                                    // Passing [] deletes the override (store rule).
                                    onChange([]);
                                }}
                            >
                                ↺
                            </Text>
                        </Tooltip>
                    </HStack>
                )}
                {collision && (
                    <Text fontSize="xs" color="red.400">{t('manage.nameCollision')}</Text>
                )}
            </Td>
        </Tr>
    );
}
